import { useCallback, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { buildLeadingScopeProps } from '@/components/filters/build-leading-scope-props'
import {
  ListBrowseToolbar,
  type ListBrowseToolbarSearchProps,
} from '@/components/filters/list-browse-toolbar'
import { SecondaryFiltersMenu } from '@/components/filters/secondary-filters-menu'
import { FilterChip } from '@/components/filters/filter-chip'
import { FilterDimensionPill } from '@/components/filters/filter-dimension-pill'
import type { FilterDefinition, ListFilterId, ListFilterLayout } from '@/components/filters/list-filter-types'
import { Button } from '@/components/ui/button'
import { useVisibleSecondaryFilters } from '@/hooks/use-visible-secondary-filters'
import {
  excludeLeadFilterKeys,
  resolveFilterDefs,
  resolveLeadFilterDef,
} from '@/lib/list-filters/resolve-filter-defs'
import { resolveListFilterLayout } from '@/lib/list-filters/resolve-list-filter-layout'
import { cn } from '@/lib/utils'

export type ListFilterBarSearchProps = ListBrowseToolbarSearchProps

/** Optional toolbar row slots — fixed order: view · filter (injected) · sort · extra. */
export type ListFilterBarToolbarSlots = {
  viewSwitcher?: ReactNode
  sortMenu?: ReactNode
  /** Page-specific trailing icons (e.g. marketplace refresh). Rendered after sort. */
  trailingExtra?: ReactNode
}

export type ListFilterBarProps<TFilters, TCtx> = {
  listId: ListFilterId
  filters: TFilters
  onFiltersChange: (next: TFilters) => void
  definitions: FilterDefinition<TFilters, TCtx>[]
  ctx: TCtx
  /** Omit for lists without a search contract — the funnel and toolbar slots dock onto the chip row. */
  search?: ListFilterBarSearchProps
  layout?: ListFilterLayout
  betweenSlot?: ReactNode
  toolbar?: ListFilterBarToolbarSlots
  hasActiveFilters?: boolean
  onClearAll?: () => void
  className?: string
  disabled?: boolean
}

function buildToolbarTrailingActions(toolbar?: ListFilterBarToolbarSlots): ReactNode {
  if (!toolbar?.sortMenu && !toolbar?.trailingExtra) return undefined
  return (
    <>
      {toolbar.sortMenu}
      {toolbar.trailingExtra}
    </>
  )
}

export function ListFilterBar<TFilters, TCtx>({
  listId,
  filters,
  onFiltersChange,
  definitions,
  ctx,
  search,
  layout: layoutOverride,
  betweenSlot,
  toolbar,
  hasActiveFilters = false,
  onClearAll,
  className,
  disabled,
}: ListFilterBarProps<TFilters, TCtx>) {
  const layout = useMemo(
    () => layoutOverride ?? resolveListFilterLayout(listId),
    [layoutOverride, listId],
  )
  const definitionsByKey = useMemo(
    () => new Map(definitions.map((def) => [def.key, def])),
    [definitions],
  )

  const leadDef = resolveLeadFilterDef(layout.lead, definitions, ctx)

  const primaryDefs = useMemo(
    () => resolveFilterDefs(excludeLeadFilterKeys(layout.primary, layout.lead), definitionsByKey, ctx),
    [layout.primary, layout.lead, definitionsByKey, ctx],
  )

  const secondaryMenuDefs = useMemo(
    () => resolveFilterDefs(excludeLeadFilterKeys(layout.secondary, layout.lead), definitionsByKey, ctx),
    [layout.secondary, layout.lead, definitionsByKey, ctx],
  )

  const { visibleKeys, add, remove, clearVisible } = useVisibleSecondaryFilters({
    secondaryKeys: layout.secondary,
    filters,
    definitionsByKey,
    ctx,
  })

  const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null)
  const pendingChipPopoverKeyRef = useRef<string | null>(null)

  const popoverProps = useMemo(
    () => ({ filters, onChange: onFiltersChange, ctx }),
    [filters, onFiltersChange, ctx],
  )

  const handlePickSecondaryFilter = useCallback((key: string) => {
    add(key)
    pendingChipPopoverKeyRef.current = key
  }, [add])

  const handleSecondaryMenuOpenChange = useCallback((menuOpen: boolean) => {
    if (menuOpen || !pendingChipPopoverKeyRef.current) return
    const key = pendingChipPopoverKeyRef.current
    pendingChipPopoverKeyRef.current = null
    requestAnimationFrame(() => {
      setOpenPopoverKey(key)
    })
  }, [])

  const handleRemoveFilter = useCallback(
    (key: string) => {
      remove(key, onFiltersChange, filters)
      setOpenPopoverKey((current) => (current === key ? null : current))
    },
    [remove, onFiltersChange, filters],
  )

  const handleClearAll = useCallback(() => {
    clearVisible()
    setOpenPopoverKey(null)
    onClearAll?.()
  }, [clearVisible, onClearAll])

  const leadOpen = leadDef != null && openPopoverKey === leadDef.key

  const handleLeadOpenChange = useCallback(
    (open: boolean) => {
      if (!leadDef) return
      setOpenPopoverKey(open ? leadDef.key : null)
    },
    [leadDef],
  )

  const handleLeadClose = useCallback(() => setOpenPopoverKey(null), [])

  const leadingScope = useMemo(
    () =>
      buildLeadingScopeProps({
        leadDef,
        filters,
        ctx,
        onFiltersChange,
        open: leadOpen,
        onOpenChange: handleLeadOpenChange,
        onClose: handleLeadClose,
        disabled,
      }),
    [leadDef, filters, ctx, onFiltersChange, leadOpen, handleLeadOpenChange, handleLeadClose, disabled],
  )

  const appliedSecondaryDefs = secondaryMenuDefs.filter((def) => visibleKeys.has(def.key))

  const showClearAll = hasActiveFilters && onClearAll
  const hasSearchRow = Boolean(search)
  const showChipRow =
    primaryDefs.length > 0 || appliedSecondaryDefs.length > 0 || showClearAll || !hasSearchRow

  const filtersMenu =
    secondaryMenuDefs.length > 0 ? (
      <SecondaryFiltersMenu
        definitions={secondaryMenuDefs}
        visibleKeys={visibleKeys}
        onPickFilter={handlePickSecondaryFilter}
        onOpenChange={handleSecondaryMenuOpenChange}
        disabled={disabled}
      />
    ) : null

  return (
    <div className={cn('space-y-2', className)}>
      {search ? (
        <ListBrowseToolbar
          {...search}
          leadingScope={leadingScope}
          viewSwitcher={toolbar?.viewSwitcher}
          filterMenu={filtersMenu}
          trailingActions={buildToolbarTrailingActions(toolbar)}
        />
      ) : null}

      {betweenSlot}

      {showChipRow ? (
        <div className="flex flex-wrap items-center gap-2">
          {primaryDefs.map((def) => (
            <FilterDimensionPill
              key={def.key}
              label={def.label}
              displayValue={def.displayValue(filters, ctx)}
              isActive={def.isNonDefault(filters, ctx)}
              open={openPopoverKey === def.key}
              onOpenChange={(open) => setOpenPopoverKey(open ? def.key : null)}
              popoverContent={
                openPopoverKey === def.key ? def.renderPopover?.(popoverProps) ?? null : null
              }
            />
          ))}

          {appliedSecondaryDefs.map((def) => (
            <FilterChip
              key={def.key}
              label={def.label}
              value={def.displayValue(filters, ctx)}
              variant="secondary"
              open={openPopoverKey === def.key}
              onOpenChange={(open) => setOpenPopoverKey(open ? def.key : null)}
              onRemove={() => handleRemoveFilter(def.key)}
              popoverContent={
                openPopoverKey === def.key ? def.renderPopover?.(popoverProps) ?? null : null
              }
            />
          ))}

          {showClearAll ? (
            <Button type="button" variant="ghostMuted" size="chip" onClick={handleClearAll}>
              Clear all
            </Button>
          ) : null}

          {!hasSearchRow && (filtersMenu || toolbar?.viewSwitcher || toolbar?.sortMenu || toolbar?.trailingExtra) ? (
            <div className="ml-auto flex shrink-0 items-center gap-1">
              {toolbar?.viewSwitcher}
              {filtersMenu}
              {buildToolbarTrailingActions(toolbar)}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
